import { mergeAttributes, Node } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import { ExternalLink, FileText, Loader2 } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import {
  Cell,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts';

import { Button } from '@/components/ui/catalyst/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';
import { useGetAgentChatCitationQuery } from '@/services/api/agentChatService';
import {
  TMaterializedCitation,
  TMaterializedCitationBlock,
} from '@/services/types/agent-chat-types';

import { SelectedCitation, useAgentChat } from '../../context/AgentChatContext';

// Citation trigger button component
interface CitationTriggerProps {
  citationId: string;
  isLoading: boolean;
  citationName?: string;
  onClick?: (event: React.MouseEvent) => void;
}

const CitationTrigger = ({
  citationId,
  isLoading,
  citationName,
  onClick,
}: CitationTriggerProps) => (
  <button
    type="button"
    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full border transition-colors bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-200 dark:bg-cyan-200 dark:text-cyan-900 dark:border-cyan-300 dark:hover:bg-cyan-100"
    onClick={onClick}
  >
    <FileText size={14} className="shrink-0" />
    {isLoading ? (
      <div className="flex items-center gap-1">
        <Loader2 size={14} className="animate-spin shrink-0" />
        <span>Citation {citationId}</span>
      </div>
    ) : (
      <span className="truncate max-w-[200px]">
        {citationName || `#${citationId}`}
      </span>
    )}
  </button>
);

// Custom tooltip for the bubble chart
const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white dark:bg-zinc-900 p-2 rounded shadow-lg text-xs border border-zinc-200 dark:border-zinc-700">
        <p className="font-medium">Page {data.x}</p>
        <p className="text-zinc-500 dark:text-zinc-400">
          {data.blockCount} citation{data.blockCount !== 1 ? 's' : ''}
        </p>
      </div>
    );
  }
  return null;
};

// Bubble chart visualization component for citations
interface CitationChartProps {
  blocksByPage: Array<{
    blocks: TMaterializedCitationBlock[];
  }>;
  onClick: (blocks: TMaterializedCitationBlock[]) => void;
}

const CitationBubbleChart = ({ blocksByPage, onClick }: CitationChartProps) => {
  // Prepare data for the scatter chart
  const chartData = blocksByPage.map(({ blocks }) => ({
    x: blocks[0].page_number + 1, // Page number on x-axis
    y: 1, // Fixed y position for horizontal alignment
    blockCount: blocks.length,
    blocks, // Original blocks for reference
  }));

  const maxBlockCount = Math.max(...chartData.map((item) => item.blockCount));
  const domain = [1, Math.max(3, maxBlockCount)]; // z-axis domain for bubble size

  // Get min and max page numbers for the domain
  const pageNumbers = chartData.map((item) => item.x);
  const minPage = Math.min(...pageNumbers);
  const maxPage = Math.max(...pageNumbers);

  // Create an array of all page numbers in the range to use as ticks
  const allPageNumbers = Array.from(
    { length: maxPage - minPage + 1 },
    (_, i) => minPage + i,
  );

  return (
    <div className="h-[60px] w-full mb-4">
      <ResponsiveContainer width="100%" height="100%">
        <ScatterChart
          margin={{ top: 0, right: 20, bottom: 0, left: 20 }}
          onClick={(data) => {
            if (data && data.activePayload && data.activePayload[0]) {
              const { payload } = data.activePayload[0];
              onClick(payload.blocks);
            }
          }}
        >
          <XAxis
            type="number"
            dataKey="x"
            name="Page"
            domain={[minPage, maxPage]}
            allowDecimals={false}
            ticks={allPageNumbers}
            label={{
              value: 'Page',
              position: 'bottom',
              offset: 10,
              style: {
                fill: 'var(--color-zinc-500)',
                fontSize: 12,
                fontWeight: 500,
              },
            }}
            tickFormatter={(value) => `${Math.round(value)}`}
          />
          <YAxis type="number" dataKey="y" hide domain={[0, 2]} />
          <ZAxis
            type="number"
            dataKey="blockCount"
            domain={domain}
            range={[20, 60]}
          />
          <RechartsTooltip content={<CustomTooltip />} />
          <Scatter data={chartData} fill="#3b82f6">
            {chartData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill="#3b82f6"
                style={{ cursor: 'pointer' }}
              />
            ))}
          </Scatter>
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  );
};

// Loading skeleton component
const SkeletonLoader = () => (
  <div className="p-4">
    <div className="h-4 bg-zinc-600/10 dark:bg-white/5 rounded animate-pulse mb-2 w-3/4" />
    <div className="h-3 bg-zinc-600/10 dark:bg-white/5 rounded animate-pulse mb-1 w-1/2" />
    <div className="h-3 bg-zinc-600/10 dark:bg-white/5 rounded animate-pulse w-2/3" />
  </div>
);

// Citation details component
interface CitationDetailsProps {
  citation: TMaterializedCitation;
  sortedBlocks: TMaterializedCitationBlock[];
  setSelectedCitation: (citation: SelectedCitation | null) => void;
}

const CitationDetails = ({
  citation,
  sortedBlocks,
  setSelectedCitation,
}: CitationDetailsProps) => {
  // Group blocks by page number
  const blocksByPage = React.useMemo(() => {
    const groups: Record<number, TMaterializedCitationBlock[]> = {};

    sortedBlocks.forEach((block) => {
      const pageNum = block.page_number + 1;
      if (!groups[pageNum]) {
        groups[pageNum] = [];
      }
      groups[pageNum].push(block);
    });

    // Convert to array and sort by page number
    return Object.entries(groups)
      .map(([pageNum, blocks]) => ({
        pageNum: parseInt(pageNum, 10),
        blocks,
      }))
      .sort((a, b) => a.pageNum - b.pageNum);
  }, [sortedBlocks]);

  // Handler for citation selection
  const handleCitationSelect = (blockId: string) => {
    setSelectedCitation({
      materializedCitation: citation,
      selectedBlockId: blockId,
    });
  };

  // Handler for bubble click
  const handleBubbleClick = (blocks: TMaterializedCitationBlock[]) => {
    if (blocks.length > 0) {
      const sortedPageBlocks = [...blocks].sort(
        (a, b) => a.bounding_box.y1 - b.bounding_box.y1,
      );
      handleCitationSelect(sortedPageBlocks[0].block_id);
    }
  };

  return (
    <div className="flex flex-col">
      <div className="p-3 border-b border-zinc-200 dark:border-zinc-800">
        <h4 className="font-medium mb-1">{citation.name}</h4>
        <div className="text-xs text-zinc-500 dark:text-zinc-400">
          {citation.mime_type}
        </div>
      </div>

      <div className="max-h-80 overflow-y-auto p-3">
        <h5 className="text-sm font-medium mb-3">Citation Locations</h5>

        {blocksByPage.length > 0 ? (
          <CitationBubbleChart
            blocksByPage={blocksByPage}
            onClick={handleBubbleClick}
          />
        ) : (
          <div className="text-sm text-zinc-500 dark:text-zinc-400">
            No citations available
          </div>
        )}
      </div>

      <div className="p-3 border-t border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900 rounded-b-md">
        <Button
          plain
          className="text-xs px-0 py-0 h-auto text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
          href={citation.url}
          target="_blank"
          rel="noopener noreferrer"
        >
          <ExternalLink size={12} />
          <span>Open source document</span>
        </Button>
      </div>
    </div>
  );
};

// Main component props
interface CitationComponentProps {
  matterId: string;
  citationId: string;
  workspaceId?: string;
  setSelectedCitation: (citation: SelectedCitation | null) => void;
}

/**
 * Pure React component for rendering a citation pill.
 * Can be used outside of Tiptap editor.
 */
export const CitationPill = ({
  matterId,
  citationId,
  workspaceId,
  setSelectedCitation,
}: CitationComponentProps) => {
  const { data: citation, isLoading } = useGetAgentChatCitationQuery({
    matterId,
    citationId,
    workspaceId,
  });
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);

  // Sort blocks by page number first, then by y0 (y1 in the bounding box)
  const sortedBlocks = useMemo(() => {
    return citation?.blocks?.slice().sort((a, b) => {
      if (a.page_number !== b.page_number) {
        return a.page_number - b.page_number;
      }
      return a.bounding_box.y1 - b.bounding_box.y1;
    });
  }, [citation?.blocks]);

  // Determine the content to render based on loading and data state
  const renderContent = () => {
    if (isLoading) {
      return <SkeletonLoader />;
    }
    if (citation) {
      return (
        <CitationDetails
          citation={citation}
          sortedBlocks={sortedBlocks || []}
          setSelectedCitation={setSelectedCitation}
        />
      );
    }

    return <SkeletonLoader />;
  };

  const handleCitationClick = (event: React.MouseEvent) => {
    // Stop propagation to prevent the click from hitting any overlay/backdrop
    // that might appear when the citation viewer modal opens
    event.stopPropagation();
    event.preventDefault();

    if (citation) {
      // If the citation is an image (e.g., jpeg/png/webp), open the source directly
      const url = citation.url || '';
      const mime = citation.mime_type || '';
      const isImage =
        mime.startsWith('image/') || /\.(jpe?g|png|gif|webp)$/i.test(url);
      if (isImage) {
        setIsImageModalOpen(true);
        return;
      }
      // Use the first block's ID if available, otherwise open viewer without a specific block
      const firstBlockId = citation.blocks?.[0]?.block_id ?? '';
      setSelectedCitation({
        materializedCitation: citation,
        selectedBlockId: firstBlockId,
      });
    }
  };

  return (
    <>
      <HoverCard openDelay={100} closeDelay={200}>
        <HoverCardTrigger asChild>
          <div className="h-full inline-block align-middle">
            <CitationTrigger
              citationId={citationId}
              isLoading={isLoading}
              citationName={citation?.name}
              onClick={handleCitationClick}
            />
          </div>
        </HoverCardTrigger>
        <HoverCardContent className="w-80 p-0" align="start">
          {renderContent()}
        </HoverCardContent>
      </HoverCard>

      {/* Image Modal for image citations */}
      {citation && isImageModalOpen ? (
        <Dialog
          open
          onOpenChange={(open) => !open && setIsImageModalOpen(false)}
        >
          <DialogContent className="w-full max-w-4xl">
            <div className="flex items-center justify-center">
              <img
                src={citation.url}
                alt={citation.name || 'Citation image'}
                className="max-h-[80vh] w-auto object-contain"
              />
            </div>
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  );
};

// Main Citation component for Tiptap
export const CitationComponent = (props: CitationComponentProps) => {
  return (
    <NodeViewWrapper className="inline-block">
      <CitationPill {...props} />
    </NodeViewWrapper>
  );
};

// Define the extension
export const Citations = Node.create({
  name: 'citation',

  inline: true,
  group: 'inline',
  inclusive: true,
  content: 'inline*',

  addOptions() {
    return {
      matterId: '1',
      threadId: '1',
    };
  },

  addAttributes() {
    return {
      citationId: {
        default: null,
        parseHTML: (element) => element.getAttribute('citation-id'),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'cite[citation-id]',
      },
      {
        tag: 'cite:empty[citation-id]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['cite', mergeAttributes(HTMLAttributes), ''];
  },

  addNodeView() {
    return ReactNodeViewRenderer(({ node }) => {
      const { setSelectedCitation } = useAgentChat();

      return (
        <CitationComponent
          citationId={node.attrs.citationId || 'unknown'}
          matterId={this.options.matterId}
          setSelectedCitation={setSelectedCitation}
        />
      );
    });
  },
});
